import {BrowserModule} from '@angular/platform-browser';
import {NgModule, NO_ERRORS_SCHEMA} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HomeComponent} from './components/home/home.component';
import {AboutUsComponent} from './components/about-us/about-us.component';
import {FooterComponent} from './components/shared/footer/footer.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ConditionsComponent} from './components/conditions/conditions.component';
import {PrivacyComponent} from './components/privacy/privacy.component';
import {PersonalDataComponent} from './components/onboarding/personal-data/personal-data.component';
import {ContactDataComponent} from './components/onboarding/contact-data/contact-data.component';
import {BaseService} from './services/base.service';
import {PhotosComponent} from './components/onboarding/photos/photos.component';
import {WebcamModule} from 'ngx-webcam';
import {FieldErrorDisplayComponent} from './components/shared/field-error-display/field-error-display.component';
import {UnsecuredService} from './services/unsecured.service';
import {LocalStorageModule} from 'angular-2-local-storage';
import {httpInterceptorProviders} from './interceptors/http-interceptor-providers';
import {HttpBackend, HttpClient, HttpClientModule} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgbDateParserFormatter, NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {CatalogsService} from './services/catalogs.service';
import {ContractsComponent} from './components/onboarding/contracts/contracts.component';
import {OnboardingComponent} from './components/onboarding/onboarding.component';
import {PageNotFoundComponent} from './components/http-status/page-not-found/page-not-found.component';
import {PasswordPatternDirective} from './directives/password-pattern.directive';
import {NumberPatternDirective} from './directives/number-pattern.directive';
import {MatchPasswordDirective} from './directives/match-password.directive';
import {IsAdultDirective} from './directives/is-adult.directive';
import {CommonComponent, ModalContentComponent} from './components/shared/common/common.component';
import {ExternalService} from './services/external.service';
import {NotificationsService} from './services/notifications.service';
import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module} from 'ng-recaptcha';
import {ReferredComponent} from './components/onboarding/referred/referred.component';
import {LoginComponent} from './components/login/login.component';
import {BalanceComponent} from './components/account/balance/balance.component';
import {AccountComponent} from './components/account/account.component';
import {MainComponent} from './components/main/main.component';
import {NgxMaskModule} from 'ngx-mask';
import {CancelRequestComponent} from './components/cancel-request/cancel-request.component';
import {AdditionalQuestionsComponent} from './components/onboarding/additional-questions/additional-questions.component';
import {AdditionalIdentificationComponent} from './components/onboarding/additional-identification/additional-identification.component';
import {NgOtpInputModule} from 'ng-otp-input';
import {OnlyLoggedInUsersGuard} from './guards/OnlyLoggedInUsers.guard';
import {TokenRenovationInterceptor} from './interceptors/token-renovation.interceptor';
import {CancellationComponent} from './components/account/cancellation/cancellation.component';
import {PhoneNumberChangeComponent} from './components/account/phone-number-change/phone-number-change.component';
import {EmailChangeComponent} from './components/account/email-change/email-change.component';
import {FaqComponent} from './components/shared/faq/faq.component';
import {ContactUsComponent} from './components/shared/contact-us/contact-us.component';
import {PasswordChangeComponent} from './components/account/password-change/password-change.component';
import {ApplicationService} from './services/application.service';
import {ContactUsAccountComponent} from './components/account/contact-us-account/contact-us-account.component';
import {TargetAccountComponent} from './components/account/target-account/target-account.component';
import {CreateBarcodeComponent} from './components/account/create-barcode/create-barcode.component';
import {TransfersComponent} from './components/account/transfers/transfers.component';
import {TextPatternDirective} from './directives/text-pattern.directive';
import {PasswordRecoveryComponent} from './components/shared/password-recovery/password-recovery.component';
import {ToastaModule} from 'ngx-toasta';
import {EmailPatternDirective} from './directives/email-pattern.directive';
import {PositiveNumberPatternDirective} from './directives/positiveNumber-pattern.directive';
import {BarcodeMinimumAmountDirective} from './directives/barcode-minimum-amount.directive';
import {IsReferredAdultDirective} from './directives/is-referred-adult.directive';
import {AddressNumberPatternDirective} from './directives/address-number-pattern.directive';
import {AddressStreetPatternDirective} from './directives/address-street-pattern.directive';
import {environment} from '../environments/environment';
import {FilterNamePipe} from './pipes/filter-name.pipe';
import {AccountStatementComponent} from './components/account/account-statement/account-statement.component';
import {FilterMonthPipe} from './pipes/filter-month.pipe';
import {NotNullDirective} from './directives/not-null.directive';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {MissingTranslationHandler, MissingTranslationHandlerParams} from '@ngx-translate/core';
import {MatchEmailDirective} from './directives/match-email.directive';
import {CustomDateParseFormatter} from './date-formatter/custom-date-parse-formatter';
import {ActivationCardHelpComponent} from './components/account/activation-card-help/activation-card-help.component';
import {EndScreenComponent} from './components/onboarding/end-screen/end-screen.component';
import {FilterBarcodeStatusPipe} from './pipes/filter-barcode-status.pipe';
import {UsaIdsScreenComponent} from './components/onboarding/usa-ids-screen/usa-ids-screen.component';
import {FilterTransferTypePipe} from './pipes/filter-transfer-type.pipe';
import {FilterTransferCardPipe} from './pipes/filter-transfer-card.pipe';
import {CalendarModule} from 'primeng/calendar';
import {ConfigurationService} from './services/configuration.service';
import {TableModule} from 'primeng/table';
import {MatchNipDirective} from './directives/match-nip.directive';
import {RippleModule} from 'primeng/ripple';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import {PasswordResetComponent} from './components/password-reset/password-reset.component';
import {EmailConfirmationComponent} from './components/email-confirmation/email-confirmation.component';
import {RedirectComponent} from './components/shared/redirect/redirect.component';
import {MyReferralsComponent} from './components/referrals/my-referrals/my-referrals.component';
import {MobileAppComponent} from './components/mobile-app/mobile-app.component';
import {OnboardingPrivacyModalComponent} from './components/shared/onboarding-privacy-modal/onboarding-privacy-modal.component';
import {ProtectedInformationComponent} from './components/shared/protected-information/protected-information.component';
import {DialogModule} from 'primeng/dialog';
import {ReferralCodeComponent} from './components/referrals/referral-code/referral-code.component';
import {AddBrokerComponent} from './components/referrals/add-broker/add-broker.component';
import {AddReferrerScreenComponent} from './components/onboarding/add-referrer-screen/add-referrer-screen.component';
import {MenubarModule} from 'primeng/menubar';
import {CardModule} from 'primeng/card';
import {DividerModule} from 'primeng/divider';
import {InputSwitchModule} from 'primeng/inputswitch';
import {TransactionHistoryComponent} from './components/account/transaction-history/transaction-history.component';
import {NewCodeComponent} from './components/account/new-code/new-code.component';
import {SelfieComponent} from './components/onboarding/selfie/selfie.component';
import {DatePipe, TitleCasePipe} from '@angular/common';
import {WebRedirectComponent} from './components/shared/web-redirect/web-redirect.component';
import {MaximumAmountPatternDirective} from './directives/maximum-amount-pattern.directive';
import {SkeletonModule} from 'primeng/skeleton';
import {BalanceNotificationsComponent} from './components/account/balance/balance-notifications/balance-notifications.component';
import {StartScreenComponent} from './components/onboarding/start-screen/start-screen.component';
import {ConditionsRemittanceComponent} from './components/remittance/conditions-remittance/conditions-remittance.component';
import {PrivacyRemittanceComponent} from './components/remittance/privacy-remittance/privacy-remittance.component';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {UsaComponent} from './components/mobile-app/usa/usa.component';
import { MexComponent } from './components/mobile-app/mex/mex.component';
import { ContactUsGenericComponent } from './components/shared/contact-us-generic/contact-us-generic.component';
import {BarcodeMaximumAmountDirective} from './directives/barcode-maximum-amount.directive';
import { HubappComponent } from './components/mobile-app/hubapp/hubapp.component';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { BarCodesCustomComponent } from './components/shared/bar-codes-custom/bar-codes-custom.component';
import { DatePickerCustomComponent } from './components/shared/date-picker-custom/date-picker-custom.component';
import { RemittanceComponent } from './components/mobile-app/remittance/remittance/remittance.component';
import { MaintenanceComponent } from './components/maintenance/maintenance.component';
import { MarkdownModule } from 'ngx-markdown';



export function httpTranslateLoaderFactory(httpBackend: HttpBackend): TranslateHttpLoader {
  return new TranslateHttpLoader(new HttpClient(httpBackend), '/assets/i18n/', '.json');
}

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    if (params.interpolateParams) {
      return params.interpolateParams['Default'] || params.key;
    }
    return params.key;
  }
}

@NgModule({
  schemas: [NO_ERRORS_SCHEMA],
  declarations: [
    AppComponent,
    HomeComponent,
    AboutUsComponent,
    FooterComponent,
    ConditionsComponent,
    PrivacyComponent,
    PersonalDataComponent,
    ContactDataComponent,
    PhotosComponent,
    FieldErrorDisplayComponent,
    ContractsComponent,
    OnboardingComponent,
    PageNotFoundComponent,
    AccountComponent,
    MainComponent,
    MatchPasswordDirective,
    PasswordPatternDirective,
    NumberPatternDirective,
    IsAdultDirective,
    CommonComponent,
    ModalContentComponent,
    LoginComponent,
    BalanceComponent,
    ReferredComponent,
    CancelRequestComponent,
    AdditionalQuestionsComponent,
    AdditionalIdentificationComponent,
    CancellationComponent,
    PhoneNumberChangeComponent,
    EmailChangeComponent,
    FaqComponent,
    ContactUsComponent,
    ContactUsAccountComponent,
    PasswordChangeComponent,
    TargetAccountComponent,
    CreateBarcodeComponent,
    TransfersComponent,
    TextPatternDirective,
    EmailPatternDirective,
    PositiveNumberPatternDirective,
    BarcodeMinimumAmountDirective,
    BarcodeMaximumAmountDirective,
    MaximumAmountPatternDirective,
    IsReferredAdultDirective,
    AddressNumberPatternDirective,
    AddressStreetPatternDirective,
    PasswordRecoveryComponent,
    FilterNamePipe,
    NotNullDirective,
    AccountStatementComponent,
    FilterMonthPipe,
    MatchEmailDirective,
    ActivationCardHelpComponent,
    EndScreenComponent,
    FilterBarcodeStatusPipe,
    UsaIdsScreenComponent,
    FilterTransferTypePipe,
    FilterTransferCardPipe,
    MatchNipDirective,
    PasswordResetComponent,
    EmailConfirmationComponent,
    RedirectComponent,
    MyReferralsComponent,
    MobileAppComponent,
    OnboardingPrivacyModalComponent,
    ProtectedInformationComponent,
    MobileAppComponent,
    ReferralCodeComponent,
    AddBrokerComponent,
    AddReferrerScreenComponent,
    TransactionHistoryComponent,
    NewCodeComponent,
    SelfieComponent,
    WebRedirectComponent,
    BalanceNotificationsComponent,
    StartScreenComponent,
    ConditionsRemittanceComponent,
    PrivacyRemittanceComponent,
    HubappComponent,
    ContactUsGenericComponent,
    UsaComponent,
    MexComponent,
    BarCodesCustomComponent,
    DatePickerCustomComponent,
    RemittanceComponent,
    MaintenanceComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    WebcamModule,
    ReactiveFormsModule,
    LocalStorageModule.forRoot({
      prefix: 'alterna-card-usa',
      storageType: 'localStorage'
    }),
    NgbModule,
    NgxPageScrollCoreModule.forRoot({ duration: 300 }),
    RecaptchaV3Module,
    NgxMaskModule.forRoot(),
    NgOtpInputModule,
    ToastaModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoaderFactory,
        deps: [HttpBackend]
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler
      }
    }),
    CalendarModule,
    TableModule,
    RippleModule,
    ProgressSpinnerModule,
    DialogModule,
    MenubarModule,
    DividerModule,
    InputSwitchModule,
    CardModule,
    SkeletonModule,
    DropdownModule,
    InputTextModule,
    MarkdownModule.forRoot()
  ],
  providers: [
    httpInterceptorProviders,
    TokenRenovationInterceptor,
    HttpClientModule,
    BaseService,
    ExternalService,
    UnsecuredService,
    CatalogsService,
    NotificationsService,
    OnlyLoggedInUsersGuard,
    ApplicationService,
    MyMissingTranslationHandler,
    ConfigurationService,
    DialogService,
    DynamicDialogModule,
    TitleCasePipe,
    {provide: NgbDateParserFormatter, useClass: CustomDateParseFormatter},
    {provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaKey },
    DatePipe
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
